import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Phone, MessageSquare, FileText } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL } from '../constants';
import { useEnquirySmsHref } from '../lib/enquiry';
import { trackEvent } from '../analytics';
import { SERVICES } from '../servicesData';
import { getScrollBehavior } from '../lib/motion';

// The five destinations (top group) — anchors resolve on Home, /about is a
// real route.
const NAV_ITEMS = [
  { href: '#services',  label: 'Services' },
  { href: '#my-work',   label: 'Gallery' },
  { href: '#catalogue', label: 'Showroom' },
  { to: '/about',       label: 'About' },
  { href: '#reviews',   label: 'Reviews' },
  { href: '#contact',   label: 'Contact' },
  { to: '/pay',         label: 'Pay by card' },
];

// Mobile menu — full-height slide-in panel from the right. Black, white
// text, purple reserved for the close/tick accents per the design system.
// Contents: the five destinations → every service page → three action rows
// (Text / Quote / Call) with the same hrefs as the sticky bar.
//
// Closes on selection, Escape and backdrop tap. Focus is trapped while
// open, aria-modal is set, the toggle carries aria-expanded, body scroll is
// locked, and focus returns to the toggle on close (Header handles that).
// Anchor links scroll to their section AFTER the panel closes.
const NavDrawer = ({ open, onClose }) => {
  const closeRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const smsHref = useEnquirySmsHref();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Focus trap — keep Tab / Shift+Tab cycling inside the panel.
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => closeRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Anchor handling: scroll AFTER the panel closes. From a service page we
  // navigate Home first, then scroll once the section has mounted — except
  // "#contact" (B02): when this page has its own form, reach THAT form so
  // typed text and the selected service survive.
  const handleNavClick = (href) => (e) => {
    trackEvent('nav_click', { target: href });
    onClose();
    if (!href.startsWith('#')) return; // real routes handled by <Link>
    e.preventDefault();
    const id = href.slice(1);
    if (location.pathname === '/' || (id === 'contact' && document.getElementById(id))) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
      }, 60);
      return;
    }
    navigate(`/${href}`);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
    }, 220);
  };

  return createPortal(
    <div
      className="nav-drawer-root"
      data-testid="nav-drawer-root"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="nav-drawer-scrim" aria-hidden="true" onClick={onClose} />
      <aside
        ref={panelRef}
        id="site-nav-drawer"
        className="nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        data-testid="nav-drawer"
      >
        <button
          ref={closeRef}
          type="button"
          className="nav-drawer-close"
          aria-label="Close menu"
          data-testid="nav-drawer-close"
          onClick={onClose}
        >
          <X size={22} strokeWidth={2.4} aria-hidden="true" />
        </button>

        <nav className="nav-drawer-nav" aria-label="Primary">
          <ul className="nav-drawer-list">
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>
                {item.to ? (
                  <Link
                    to={item.to}
                    className="nav-drawer-link"
                    data-testid={item.to === '/about' ? 'nav-drawer-link-about-page' : `nav-drawer-link-${item.to.slice(1)}`}
                    onClick={() => {
                      trackEvent('nav_click', { target: item.to });
                      onClose();
                    }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    className="nav-drawer-link"
                    data-testid={`nav-drawer-link-${item.href.slice(1)}`}
                    onClick={handleNavClick(item.href)}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Every service page — data pulled from servicesData.SERVICES. */}
          <p className="nav-drawer-group-label" aria-hidden="true">Services</p>
          <ul className="nav-drawer-list nav-drawer-list--sub" aria-label="Service pages">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={s.path}
                  className="nav-drawer-link nav-drawer-link--sub"
                  data-testid={`nav-drawer-service-${s.slug}`}
                  onClick={() => {
                    trackEvent('nav_drawer_service_click', { location: s.slug });
                    onClose();
                  }}
                >
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Three action rows — same hrefs as the sticky bar. */}
        <div className="nav-drawer-actions" aria-label="Quick contact">
          <a
            href={smsHref}
            className="nav-drawer-action"
            data-testid="nav-drawer-sms"
            onClick={() => {
              trackEvent('contact_text', { location: 'drawer' });
              trackEvent('sms_click', { location: 'drawer' });
              onClose();
            }}
          >
            <MessageSquare size={18} strokeWidth={2.2} aria-hidden="true" />
            <span>Text</span>
          </a>
          <a
            href="#contact"
            className="nav-drawer-action nav-drawer-action--primary"
            data-testid="nav-drawer-quote"
            onClick={(e) => {
              trackEvent('contact_quote', { location: 'drawer' });
              trackEvent('quote_cta_click', { location: 'drawer' });
              handleNavClick('#contact')(e);
            }}
          >
            <FileText size={18} strokeWidth={2.2} aria-hidden="true" />
            <span>Quote</span>
          </a>
          <a
            href={PHONE_TEL}
            className="nav-drawer-action"
            data-testid="nav-drawer-call"
            onClick={() => trackEvent('call_click', { location: 'drawer' })}
          >
            <Phone size={20} strokeWidth={2.2} aria-hidden="true" />
            <span>{`Call ${PHONE_DISPLAY}`}</span>
          </a>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default NavDrawer;
